/**
 * Text to evenly-spaced outline points. Vendored (trimmed) from the swarm
 * sim's sources.ts: rasterize to a canvas, chain the boundary pixels into
 * ordered loops, resample each loop by ARC LENGTH to exactly `count` points,
 * so spacing is uniform and no two points ever stack.
 *
 * Deliberately decoupled from web-swarm-sim (Free's rule: studio changes
 * must never break the main page).
 */

type InsideFn = (i: number) => boolean;
type Pt = [number, number];

function collectOutline(inside: InsideFn, W: number, H: number, count: number): Pt[] {
  const edges: number[] = [];
  for (let y = 0; y < H; y++) {
    const row = y * W;
    for (let x = 0; x < W; x++) {
      const i = row + x;
      if (!inside(i)) continue;
      if (
        x === 0 || y === 0 || x === W - 1 || y === H - 1 ||
        !inside(i - 1) || !inside(i + 1) || !inside(i - W) || !inside(i + W)
      ) edges.push(i);
    }
  }
  if (edges.length === 0) return [];

  // spatial grid for nearest-unvisited lookup while chaining
  const CELL = 3, inv = 1 / CELL, R2 = 3 * 3;
  const grid = new Map<number, number[]>();
  const gkey = (cx: number, cy: number) => cx * 46341 + cy;
  for (const i of edges) {
    const k = gkey(Math.floor((i % W) * inv), Math.floor(((i / W) | 0) * inv));
    let a = grid.get(k);
    if (!a) { a = []; grid.set(k, a); }
    a.push(i);
  }
  const visited = new Uint8Array(W * H);
  const nearest = (x: number, y: number) => {
    const cx = Math.floor(x * inv), cy = Math.floor(y * inv);
    let best = -1, bd = R2 + 1;
    for (let gx = cx - 1; gx <= cx + 1; gx++)
      for (let gy = cy - 1; gy <= cy + 1; gy++) {
        const a = grid.get(gkey(gx, gy));
        if (!a) continue;
        for (const j of a) {
          if (visited[j]) continue;
          const d = (j % W - x) ** 2 + (((j / W) | 0) - y) ** 2;
          if (d < bd) { bd = d; best = j; }
        }
      }
    return bd <= R2 ? best : -1;
  };

  const chains: Pt[][] = [];
  for (const start of edges) {
    if (visited[start]) continue;
    const chain: Pt[] = [];
    let cur = start;
    while (cur >= 0) {
      visited[cur] = 1;
      const x = cur % W, y = (cur / W) | 0;
      chain.push([x, y]);
      cur = nearest(x, y);
    }
    chains.push(chain);
  }

  const lens = chains.map((ch) => {
    let L = 0;
    for (let i = 1; i < ch.length; i++) L += Math.hypot(ch[i][0] - ch[i - 1][0], ch[i][1] - ch[i - 1][1]);
    return Math.max(L, 1e-3);
  });
  const total = lens.reduce((a, b) => a + b, 0);
  const alloc = chains.map((_, i) => Math.max(1, Math.round((count * lens[i]) / total)));
  let gi = 0;
  for (let i = 1; i < alloc.length; i++) if (alloc[i] > alloc[gi]) gi = i;
  alloc[gi] = Math.max(1, alloc[gi] + (count - alloc.reduce((a, b) => a + b, 0)));

  const out: Pt[] = [];
  for (let c = 0; c < chains.length && out.length < count; c++) {
    const ch = chains[c], m = alloc[c];
    if (m <= 0) continue;
    if (ch.length === 1) {
      for (let k = 0; k < m; k++) out.push([ch[0][0], ch[0][1]]);
      continue;
    }
    const cum = [0];
    for (let i = 1; i < ch.length; i++)
      cum.push(cum[i - 1] + Math.hypot(ch[i][0] - ch[i - 1][0], ch[i][1] - ch[i - 1][1]));
    const Lc = cum[cum.length - 1];
    for (let k = 0; k < m; k++) {
      const t = (m === 1 ? 0.5 : k / m) * Lc;
      let seg = 1;
      while (seg < cum.length && cum[seg] < t) seg++;
      if (seg >= cum.length) seg = cum.length - 1;
      const a = cum[seg - 1], b = cum[seg];
      const f = b > a ? (t - a) / (b - a) : 0;
      const p0 = ch[seg - 1], p1 = ch[seg];
      out.push([p0[0] + (p1[0] - p0[0]) * f, p0[1] + (p1[1] - p0[1]) * f]);
    }
  }
  while (out.length < count) out.push(out.length ? out[out.length - 1] : [W / 2, H / 2]);
  return out.slice(0, count);
}

/**
 * Sample `text` in `family` to exactly `count` flat outline points, centered
 * on the origin in the xy plane (z=0), height `targetH` scene units, width
 * capped at `maxW` (height shrinks to keep the glyph aspect).
 */
export function sampleTextOutline(
  text: string,
  count: number,
  family: string,
  targetH = 3.4,
  maxW = 14
): Float32Array {
  const label = text.length ? text : " ";
  const fontPx = 220;
  const probe = document.createElement("canvas").getContext("2d")!;
  probe.font = `${fontPx}px ${family}`;
  const pad = 60;
  const w = Math.max(1, Math.ceil(probe.measureText(label).width)) + pad * 2;
  const h = fontPx + pad * 2;

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, w, h);
  ctx.font = `${fontPx}px ${family}`;
  ctx.fillStyle = "#fff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, w / 2, h / 2);

  const px = ctx.getImageData(0, 0, w, h).data;
  const inside: InsideFn = (i) => px[i * 4 + 3] > 60;
  const pts = collectOutline(inside, w, h, count);

  // tight bounds of the sampled points so sizing tracks the INK, not the canvas
  let minx = Infinity, maxx = -Infinity, miny = Infinity, maxy = -Infinity;
  for (const [x, y] of pts) {
    if (x < minx) minx = x;
    if (x > maxx) maxx = x;
    if (y < miny) miny = y;
    if (y > maxy) maxy = y;
  }
  const iw = Math.max(1, maxx - minx), ih = Math.max(1, maxy - miny);
  let sh = targetH, sw = targetH * (iw / ih);
  if (sw > maxW) { const k = maxW / sw; sw *= k; sh *= k; }
  const cx = (minx + maxx) / 2, cy = (miny + maxy) / 2;

  const positions = new Float32Array(count * 3);
  for (let n = 0; n < count; n++) {
    const p = pts[n];
    positions[n * 3] = ((p[0] - cx) / iw) * sw;
    positions[n * 3 + 1] = ((cy - p[1]) / ih) * sh;
    positions[n * 3 + 2] = 0;
  }
  return positions;
}
